import { useQuery } from '@tanstack/react-query'

import { type AppTimezonePreference } from '@sparkai/utils'

import { apiClient } from '../../apiClient'
import { getSelectedTimezoneHeaders, SPARK_SELECTED_TIMEZONE_HEADER } from '../../timezoneRequestContext'

type CurrentAndPrevious<T> = { current: T; previous: T }

type TaskCompletionSourceReport = {
  programId: string
  taskCount: CurrentAndPrevious<number>
  completionTimeSampleCount: CurrentAndPrevious<number>
  avgCompletionTimeMs: CurrentAndPrevious<number | null>
  breakdown: Record<
    string,
    { taskCount: number; completionTimeSampleCount: number; avgCompletionTimeMs: number | null }
  >
}

export type ProgramInstanceTaskCompletionResponse = {
  programInstanceId: string
  live: TaskCompletionSourceReport
  recycler: TaskCompletionSourceReport | null
}

type Granularity = 'daily' | 'weekly' | 'monthly'

export type TaskCompletionMetric<T = number> = { value: T; trend: number }

/**
 * One chart point with the average completion time of each source in milliseconds. `start` and `end`
 * are breakdown bucket keys; they differ only in the weekly view, where seven daily buckets are
 * combined into one point.
 */
export type TaskCompletionBucket = {
  start: string
  end: string
  live: number | null
  recycled: number | null
}

export type ProgramInstanceTaskCompletion = {
  hasRecycler: boolean
  tasks: TaskCompletionMetric
  liveAvgCompletionTimeMs: TaskCompletionMetric<number | null>
  recycledAvgCompletionTimeMs: TaskCompletionMetric<number | null>
  completionTimeBuckets: TaskCompletionBucket[]
}

const DAYS_PER_WEEK = 7

export const PROGRAM_TASK_COMPLETION_STATS_KEY = 'program-task-completion-stats'

function getDailyBuckets(response: ProgramInstanceTaskCompletionResponse): TaskCompletionBucket[] {
  return Object.keys(response.live.breakdown)
    .sort((a, b) => a.localeCompare(b))
    .map((timeslice) => ({
      start: timeslice,
      end: timeslice,
      live: response.live.breakdown[timeslice].avgCompletionTimeMs,
      recycled: response.recycler?.breakdown[timeslice]?.avgCompletionTimeMs ?? null,
    }))
}

/**
 * Keeps the weekly chart formula: the sum of the week's daily averages divided by seven, even for a
 * final group with fewer than seven days. A day without samples counts as zero; a week without any
 * sample in a source is a gap (`null`) instead of 0.
 */
function getWeeklyAverage(values: (number | null)[]) {
  const measured = values.filter((value): value is number => value !== null)
  if (measured.length === 0) return null

  const sum = measured.reduce((acc, value) => acc + value, 0)
  return Number.parseFloat((sum / DAYS_PER_WEEK).toFixed(2))
}

function groupBucketsByWeek(dailyBuckets: TaskCompletionBucket[]): TaskCompletionBucket[] {
  const weeks: TaskCompletionBucket[] = []

  for (let i = 0; i < dailyBuckets.length; i += DAYS_PER_WEEK) {
    const week = dailyBuckets.slice(i, i + DAYS_PER_WEEK)
    weeks.push({
      start: week[0].start,
      end: week.at(-1)?.end ?? week[0].end,
      live: getWeeklyAverage(week.map((day) => day.live)),
      recycled: getWeeklyAverage(week.map((day) => day.recycled)),
    })
  }

  return weeks
}

/** A missing or zero baseline has no defined percentage change. */
function getPercentageChange(current: number | null, previous: number | null): number {
  if (current === null || previous === null || previous === 0) return 0
  return ((current - previous) / previous) * 100
}

export function toProgramInstanceTaskCompletion(
  response: ProgramInstanceTaskCompletionResponse,
  granularity?: Granularity
): ProgramInstanceTaskCompletion {
  const { live, recycler } = response
  const dailyBuckets = getDailyBuckets(response)
  const currentTasks = live.taskCount.current + (recycler?.taskCount.current ?? 0)
  const previousTasks = live.taskCount.previous + (recycler?.taskCount.previous ?? 0)

  return {
    hasRecycler: recycler !== null,
    tasks: { value: currentTasks, trend: getPercentageChange(currentTasks, previousTasks) },
    liveAvgCompletionTimeMs: {
      value: live.avgCompletionTimeMs.current,
      trend: getPercentageChange(live.avgCompletionTimeMs.current, live.avgCompletionTimeMs.previous),
    },
    recycledAvgCompletionTimeMs: {
      value: recycler?.avgCompletionTimeMs.current ?? null,
      trend: recycler
        ? getPercentageChange(recycler.avgCompletionTimeMs.current, recycler.avgCompletionTimeMs.previous)
        : 0,
    },
    completionTimeBuckets: granularity === 'weekly' ? groupBucketsByWeek(dailyBuckets) : dailyBuckets,
  }
}

export function useGetTaskCompletion(
  programInstanceId?: string,
  startDate?: string,
  endDate?: string,
  granularity?: Granularity,
  timezonePreference?: AppTimezonePreference
) {
  const timezoneHeaders = timezonePreference ? getSelectedTimezoneHeaders(timezonePreference) : undefined

  return useQuery({
    queryKey: [
      PROGRAM_TASK_COMPLETION_STATS_KEY,
      programInstanceId,
      startDate,
      endDate,
      granularity,
      timezoneHeaders?.[SPARK_SELECTED_TIMEZONE_HEADER],
    ],
    queryFn: async () => {
      const { data } = await apiClient.get<ProgramInstanceTaskCompletionResponse>(
        `/int/v3/program-instances/${programInstanceId}/reports/task-completion`,
        {
          params: { startDate, endDate, granularity },
          ...(timezoneHeaders ? { headers: timezoneHeaders } : {}),
        }
      )

      return data
    },
    select: (data) => toProgramInstanceTaskCompletion(data, granularity),
    enabled: Boolean(programInstanceId) && Boolean(startDate) && Boolean(endDate) && Boolean(granularity),
    refetchOnWindowFocus: false,
  })
}
